import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const ServicesSection = styled.section`
  padding: 80px 0;
  background: var(--background-light);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;

  .icon {
    width: 8px;
    height: 8px;
    background: var(--secondary-color);
    border-radius: 50%;
  }

  span {
    color: var(--secondary-color);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.9rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--secondary-color);
  }
`;

const ServiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--secondary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  margin-bottom: 20px;
`;

const ServiceNumber = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-light);
  font-family: "Phudu", sans-serif;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--text-dark);
  margin-bottom: 15px;
  font-family: "Phudu", sans-serif;
`;

const ServiceDescription = styled.p`
  color: var(--text-light);
  line-height: 1.6;
  margin-bottom: 25px;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 30px;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    font-size: 0.95rem;
    color: var(--text-light);

    .icon {
      width: 6px;
      height: 6px;
      background: var(--secondary-color);
      border-radius: 50%;
      flex-shrink: 0;
    }
  }
`;

const ServiceImage = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 15px;
  overflow: hidden;
  margin-bottom: 25px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  ${ServiceCard}:hover & img {
    transform: scale(1.05);
  }
`;

const ServiceLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--secondary-color);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;

  &:hover {
    gap: 12px;
    color: #c9a660;
  }

  i {
    transition: transform 0.3s ease;
  }

  &:hover i {
    transform: translateX(4px);
  }
`;

const Services = () => {
  const services = [
    {
      id: 1,
      title: "New Containers",
      description:
        "Looking for a fresh start? Our new one-trip containers are the perfect choice for long-term use.",
      icon: "fas fa-shipping-fast",
      image: "/assets/2025/08/20ft-office-container-with-no-a-c-600x450.jpg",
      features: [
        "Clean, rust-free, and ready to go",
        "Ideal for businesses, construction sites, and residential projects",
        "Available in multiple sizes: 10ft, 20ft, 40ft, and more",
      ],
      link: "/services/new-containers",
    },
    {
      id: 2,
      title: "Used Containers",
      description:
        "Affordable and dependable, our used containers are carefully inspected to ensure durability.",
      icon: "fas fa-recycle",
      image: "/assets/2025/08/30ft-office-container-300x300.jpg",
      features: [
        "Wind and watertight certified",
        "Cost-effective solution for personal or business storage",
        "Available in multiple grades and sizes",
      ],
      link: "/services/used-containers",
    },
    {
      id: 3,
      title: "Custom Modifications",
      description:
        "Turn your container into exactly what you need. We specialize in:",
      icon: "fas fa-tools",
      image: "/assets/2025/08/40ft-office-with-toilet-600x450.jpg",
      features: [
        "Side doors, roll-up doors, and extra access points",
        "Windows, vents, and insulation",
        "Paint, branding, and interior finishes",
        "Conversions into offices, workshops, or living spaces",
      ],
      link: "/services/custom-modifications",
    },
  ];

  return (
    <ServicesSection>
      <Container>
        <SectionHeader>
          <SectionTag>
            <div className="icon"></div>
            <span>Our Services</span>
          </SectionTag>

          <SectionTitle>
            From sales to delivery, we got you covered
          </SectionTitle>

          <SectionDescription>
            At <strong>Baypoint Storage</strong>, we provide more than just
            containers—we deliver <strong>solutions</strong> for storage,
            shipping, business, and personal projects.
          </SectionDescription>
        </SectionHeader>

        <ServicesGrid>
          {services.map((service) => (
            <ServiceCard key={service.id}>
              <ServiceHeader>
                <ServiceIcon>
                  <i className={service.icon}></i>
                </ServiceIcon>
                <ServiceNumber>0{service.id}.</ServiceNumber>
              </ServiceHeader>

              <ServiceTitle>{service.title}</ServiceTitle>

              <ServiceDescription>{service.description}</ServiceDescription>

              <ServiceImage>
                <img src={service.image} alt={service.title} />
              </ServiceImage>

              <ServiceFeatures>
                {service.features.map((feature, index) => (
                  <li key={index}>
                    <div className="icon"></div>
                    {feature}
                  </li>
                ))}
              </ServiceFeatures>

              <ServiceLink to={service.link}>
                Read More
                <i className="fas fa-chevron-right"></i>
              </ServiceLink>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Container>
    </ServicesSection>
  );
};

export default Services;
